// A house at your harbour: the offer, the terms, and the two answers.
//
// The card in the city panel shows the offer when the player selects the
// harbour. This is the one they cannot miss — a foreign house sails in every
// five to eight minutes and waits a minute for an answer, so the offer has to
// announce itself.
import { Button } from '@aod/ui/components/ui/button';
import { useState } from 'react';
import { merchantOfferKey } from '../dynasty/pendingOffers.js';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { houseName } from '../sea/houseNames.js';
import type { SeaOffer } from '../sea/offer.js';
import { AutoFade } from './AutoFade.js';
import { merchantScene } from './sceneEvents.js';
import { sceneUrls } from './scenes.js';

export function MerchantOfferToast({
  cityId,
  offer,
  onAccept,
  onDecline,
  matchOver = false,
}: {
  /** The harbour with the offer on the table. */
  readonly cityId: string;
  readonly offer: SeaOffer | null;
  readonly onAccept: (cityId: string) => void;
  readonly onDecline: (cityId: string) => void;
  /** The match has ended: no offer is worth a card over the result. */
  readonly matchOver?: boolean;
}) {
  const t = useT();
  // The card fades after ~10 s so it stops covering the board; the offer
  // itself stays on the harbour (and the Diplomacy tab) until it lapses.
  const [goneKey, setGoneKey] = useState<string | null>(null);
  if (!offer || matchOver) return null;
  const key = merchantOfferKey({ cityId, offer });
  if (goneKey === key) return null;
  const house = houseName(offer.house);
  return (
    // Its own lane, below the attack toasts: both are `fixed` in the same
    // view, and an attack card and an offer card must never land on top of
    // one another.
    <div
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--hud-panel-top)+6.5rem)] z-50 flex flex-col items-center gap-2 px-3"
      aria-live="polite"
    >
      <AutoFade key={key} onGone={() => setGoneKey(key)}>
        <div
          data-testid="merchant-offer-toast"
          {...appDom({ id: 'merchant-offer-toast', type: 'Toast', scope: 'toast' })}
          className="pointer-events-auto flex w-full max-w-[min(92vw,460px)] flex-wrap items-center gap-3 rounded-xl border border-sky-500/50 bg-panel-scrim px-4 py-2.5 text-left text-foreground shadow-[0_0_24px_rgba(120,190,255,0.16)] backdrop-blur-sm dark:shadow-[0_0_30px_rgba(120,190,255,0.22)]"
        >
          <img
            src={sceneUrls(merchantScene().group, merchantScene().id).srcSmall}
            alt=""
            loading="lazy"
            className="h-11 w-[4.5rem] shrink-0 rounded border border-sky-500/30 object-cover"
            data-testid="merchant-offer-scene"
          />
          <span className="flex min-w-[12rem] flex-1 flex-col">
            <span className="font-serif leading-tight text-sm text-sky-700 dark:text-sky-300">
              {t('sea.offerTitle')}
            </span>
            <span className="text-xs leading-snug text-muted-foreground">
              {t('sea.offerTerms', {
                house: t(house),
                gold: offer.goldPerTick,
                grain: offer.grainPerTick,
                gift: offer.gift,
              })}
            </span>
          </span>
          <span className="flex shrink-0 gap-2">
            <Button type="button" size="sm" onClick={() => onAccept(cityId)}>
              {t('sea.accept')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onDecline(cityId)}>
              {t('sea.decline')}
            </Button>
          </span>
        </div>
      </AutoFade>
    </div>
  );
}
